import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import { initKeycloak } from "./services/keycloak";
import { ErrorProvider } from "./errors/errorContext";
import { SuccessProvider } from "./success/successContext";
import { ForbiddenProvider } from "./permissions/forbiddenContext";
import ErrorGlobalAlert from "./errors/ErrorGlobalAlert";
import SuccessGlobalAlert from "./success/SuccessGlobalAlert";
import Box from "@mui/material/Box";

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
);

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  if (error && typeof error === "object") {
    const errorWithMessage = error as {
      message?: unknown;
      error?: unknown;
      error_description?: unknown;
    };

    const candidateMessages = [
      errorWithMessage.message,
      errorWithMessage.error_description,
      errorWithMessage.error,
    ];

    for (const candidate of candidateMessages) {
      if (typeof candidate === "string" && candidate.trim()) {
        return candidate;
      }
    }

    try {
      return JSON.stringify(error);
    } catch {
      return "Authentication initialization failed. Please refresh the page or contact support.";
    }
  }

  return "Authentication initialization failed. Please refresh the page or contact support.";
};

const renderAuthError = (error: unknown) => {
  const message = getErrorMessage(error);

  root.render(
    <React.StrictMode>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f8fafc",
          color: "#0f172a",
          padding: "24px",
        }}
      >
        <div
          style={{
            maxWidth: "480px",
            width: "100%",
            backgroundColor: "#ffffff",
            border: "1px solid #cbd5e1",
            borderRadius: "12px",
            padding: "24px",
            boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
          }}
        >
          <h1 style={{ margin: "0 0 12px", fontSize: "1.5rem" }}>
            Authentication initialization failed
          </h1>
          <p style={{ margin: "0 0 16px", lineHeight: 1.5 }}>{message}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              border: 0,
              borderRadius: "8px",
              backgroundColor: "#0f172a",
              color: "#ffffff",
              padding: "10px 16px",
              cursor: "pointer",
              fontSize: "0.95rem",
            }}
          >
            Refresh page
          </button>
        </div>
      </Box>
    </React.StrictMode>,
  );
};

initKeycloak()
  .then(() => {
    root.render(
      <React.StrictMode>
        <ErrorProvider>
          <SuccessProvider>
            <ForbiddenProvider>
              <ErrorGlobalAlert />
              <SuccessGlobalAlert />
              <App />
            </ForbiddenProvider>
          </SuccessProvider>
        </ErrorProvider>
      </React.StrictMode>,
    );
  })
  .catch((err) => {
    console.error("Keycloak initialization failed:", err);
    renderAuthError(err);
  });
